import { Prompt } from "react-router-dom"
import { useCallback, useEffect, useState } from "react"
import { Alert, Button, Checkbox, Input, Select, Space, Tag, message } from "antd"
import type { CatalogRevision } from "../../../types/regulatory"
import { regulatoryApi } from "../../../utils/regulatoryApi"
import { useCatalogResource } from "../useCatalog"
import { ResourceState } from "./CatalogUI"
export function PresentationEditor({
  revision,
  editable,
}: {
  revision: CatalogRevision
  editable: boolean
}) {
  const resource = useCatalogResource(
    useCallback(
      (signal) => regulatoryApi.resolved(revision.templateUuid, revision.uuid, signal),
      [revision.templateUuid, revision.uuid],
    ),
  )
  const [assignments, setAssignments] = useState<Record<string, string>>({}),
    [reviewed, setReviewed] = useState(false),
    [saving, setSaving] = useState(false),
    [query, setQuery] = useState(""),
    [selected, setSelected] = useState<string[]>([])
  const p = resource.data?.presentation
  useEffect(() => {
    setAssignments({ ...p?.assignments })
    setReviewed(p?.reviewed ?? false)
    setSelected([])
  }, [p])
  const canEdit = editable && !!p
  const categories = p?.categories ?? []
  const scope = revision.definition.scope === "site" ? "site" : "equipment"
  const options = categories
    .filter((c) => c.scope === scope || c.scope === "any")
    .map((c) => ({ value: c.code, label: c.label }))
  const points = Array.from(
    new Map((resource.data?.controls ?? []).map((c) => [c.point.uuid, c.point])).values(),
  ).filter((c) =>
    `${c.definition.section} ${c.definition.label}`
      .toLocaleLowerCase()
      .includes(query.toLocaleLowerCase()),
  )
  const dirty =
    JSON.stringify(assignments) !== JSON.stringify(p?.assignments ?? {}) ||
    reviewed !== (p?.reviewed ?? false) ||
    p?.suggested
  const publish = async () => {
    if (!p) return
    setSaving(true)
    try {
      await regulatoryApi.publishPresentation(revision.templateUuid, revision.uuid, {
        expectedProfileUuid: p.uuid,
        reviewed,
        assignments,
      })
      message.success("Classement publié")
      resource.reload()
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }
  return (
    <ResourceState {...resource} retry={resource.reload}>
      <>
        <Prompt
          when={
            canEdit &&
            !saving &&
            (JSON.stringify(assignments) !== JSON.stringify(p?.assignments ?? {}) ||
              reviewed !== (p?.reviewed ?? false))
          }
          message="Le classement n’est pas publié. Quitter et abandonner ces modifications ?"
        />
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {resource.data && !p && (
            <Alert
              type="error"
              message="API de classement indisponible"
              description="Appliquez la migration du 20/10/2026 et redémarrez l’API avant de classer les contrôles."
            />
          )}
          <Alert
            type={p?.reviewed ? "success" : "warning"}
            message={
              p?.reviewed
                ? `Classement revu · version ${p.version}`
                : p?.suggested
                  ? "Proposition de classement — à vérifier"
                  : "Classement opérationnel non revu"
            }
            description="Les catégories organisent l’affichage. Elles ne changent ni les définitions réglementaires, ni les réponses, ni le statut de revue des modèles."
          />
          <Space wrap>
            {categories
              .filter((c) => c.scope === scope || c.scope === "any")
              .map((c) => (
                <Tag key={c.code}>
                  <img alt="" width={22} height={22} src={`/icones/${c.iconKey}.png`} /> {c.label}
                </Tag>
              ))}
          </Space>
          <Input
            aria-label="Rechercher pour classer"
            value={query}
            placeholder="Filtrer par section ou intitulé"
            onChange={(e) => setQuery(e.target.value)}
          />
          {canEdit && (
            <Space wrap>
              <Button onClick={() => setSelected(points.map((c) => c.uuid))}>
                Sélectionner les points affichés
              </Button>
              <Button onClick={() => setSelected([])}>Désélectionner</Button>
              <Select
                aria-label="Catégorie en lot"
                placeholder="Classer la sélection"
                style={{ minWidth: 240 }}
                options={options}
                value={undefined}
                disabled={!selected.length || saving}
                onChange={(code: string) =>
                  setAssignments((a) => ({
                    ...a,
                    ...Object.fromEntries(selected.map((id) => [id, code])),
                  }))
                }
              />
              <span>{selected.length} sélectionnés</span>
            </Space>
          )}
          {points.map((point) => (
            <div
              key={point.uuid}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                width: "100%",
                flexWrap: "wrap",
                borderBottom: "1px solid #eee",
                padding: "10px 0",
              }}
            >
              {canEdit && (
                <Checkbox
                  aria-label={`Sélectionner : ${point.definition.label}`}
                  checked={selected.includes(point.uuid)}
                  disabled={saving}
                  onChange={(e) =>
                    setSelected((s) =>
                      e.target.checked ? [...s, point.uuid] : s.filter((id) => id !== point.uuid),
                    )
                  }
                />
              )}
              <div style={{ flex: "1 1 260px" }}>
                <strong>{point.definition.label}</strong>
                <div>
                  {point.definition.section} · {point.definition.controlCode || point.definition.id}
                </div>
              </div>
              <Select
                aria-label={`Catégorie : ${point.definition.label}`}
                options={options}
                style={{ width: 270, maxWidth: "100%" }}
                disabled={!canEdit || saving}
                value={assignments[point.uuid] ?? "unclassified"}
                onChange={(code: string) => setAssignments((a) => ({ ...a, [point.uuid]: code }))}
              />
            </div>
          ))}
          {canEdit && (
            <Space wrap>
              <Checkbox
                checked={reviewed}
                disabled={saving}
                onChange={(e) => setReviewed(e.target.checked)}
              >
                Classement vérifié en interne
              </Checkbox>
              <Button type="primary" loading={saving} disabled={!dirty} onClick={publish}>
                Publier le classement
              </Button>
            </Space>
          )}
          {p?.uuid && (
            <small>
              Profil : {p.uuid} · v{p.version}. Chaque publication conserve les versions
              précédentes.
            </small>
          )}
        </Space>
      </>
    </ResourceState>
  )
}
